import React from 'react'
import {
  mergeUpdate,
  logger
} from '../utils'
import { isEmpty } from 'lodash'

import SubscriptionManager from '../connection/subscription'
import SportManager from '../managers/sport'

import {
  useUpdateEffect
} from '../ui'

import {
  EVENT_MARKET_TYPE_UPDATE,
  EVENT_SCORE_UPDATE,
  EVENT_STATE_UPDATE,
  EVENT_SET
} from '../actions'
import Bus from '../bus'

export const EventContext = React.createContext()

export function useEventState() {
  return React.useContext(EventContext) || eventInitialState
}

export const eventInitialState = {
  loading: true,
  initialized: false
}

/*
  Should become cleaner in future
*/
export const eventReducer = (eventState, action) => {
  switch (action.type) {
    case EVENT_SCORE_UPDATE:
      return {
        ...eventState,
        scoreInfo: action.payload
      }
    case EVENT_STATE_UPDATE:
      return mergeUpdate(
        eventState,
        action.payload
      )
    case EVENT_MARKET_TYPE_UPDATE:
      return {
        ...eventState,
        marketTypes: {
          ...eventState.marketTypes,
          ...action.payload.marketTypes
        }
      }
    case EVENT_SET:
      return action.payload
    default:
      return eventState
  }
}

const createEventInitialState = event => {
  if (isEmpty(event)) {
    return eventInitialState
  }

  return {
    ...event,
    loading: false,
    initialized: true
  }
}

/*
  Hook for init own EventState with default subscriptions (for syncing data)
*/
export const createEventState = (event) => {
  const [
    eventState,
    eventDispatcher
  ] = React.useReducer(
    eventReducer,
    event,
    createEventInitialState
  )

  // Using update effect for outer event updates retranslation
  useUpdateEffect(
    () => {
      if (isEmpty(event)) {
        return
      }

      logger.debug(
        `[event:${event.id}]`,
        'updated event state from provided outer event'
      )

      eventDispatcher({
        type: EVENT_STATE_UPDATE,
        payload: {
          ...event,
          loading: false,
          initialized: true
        }
      })
    },
    [event]
  )

  React.useEffect(
    () => {
      if (!eventState.id) { return }

      const eventUpdateHandler = data => {
        Bus.emit(Bus.events.sport.sportEventUpdate, data.payload)
        eventDispatcher(data)
      }

      SportManager.dataBroadcaster.subscribeToEventUpdates(
        eventState.id,
        eventUpdateHandler
      )

      return () => {
        SportManager.dataBroadcaster.unsubscribeFromEventUpdates(
          eventState.id,
          eventUpdateHandler
        )
      }
    },
    [eventState?.id]
  )

  return [
    eventState,
    eventDispatcher
  ]
}

/*
  Event provider
  Using in common cases
  Registering event dispatcher for internal updates
  Do not subscribing to notifier
*/
export function EventProvider(props) {
  const {
    event,
    children
  } = props

  const [eventState] = createEventState(event)

  return (
    <EventContext.Provider value={eventState}>
      {children}
    </EventContext.Provider>
  )
}

/*
  Event provider
  Using in special cases when there is no parent subscribed tournament state
  Registering event dispatcher for internal updates
  Got dedicated event subscribtion to notifier
*/
export function DedicatedEventProvider(props) {
  const {
    event,
    children
  } = props

  const [eventState] = createEventState(event)

  React.useEffect(
    () => {
      if (!eventState.id) { return }

      // Subscribe on sockets Event Odds Updates
      // If its live - also subscribing for Score Updates
      const eventSubscriptionTopics = [
        {
          topic: SubscriptionManager.subscriptionTopics.EXPERIMENTAL_EVENT,
          key: eventState.id
        }
      ]

      SubscriptionManager.notifierSubscribe(eventSubscriptionTopics)

      return () => {
        SubscriptionManager.notifierUnsubscribe(eventSubscriptionTopics)
      }
    },
    [eventState.id]
  )

  return (
    <EventContext.Provider value={eventState}>
      {children}
    </EventContext.Provider>
  )
}
